import { useEffect, useRef, useState, useCallback } from "react";
import { fireSwal } from "../utils/swal";
import { createPedido } from "../services/pedidosService";
import { RpcClientError } from "../services/rpcClient";
import {
  listarSucursalesDelPortal,
  obtenerConfiguracionSucursal,
} from "../services/sucursalesService";
import {
  obtenerUbicacionActual,
  calcularSucursalMasCercana,
  obtenerSucursalPorDefecto,
  geolocationDisponible,
} from "../services/ubicacionService";
import {
  leerSucursalIdPersistida,
  guardarSucursalIdPersistida,
} from "../utils/sucursalPersistencia";
import {
  mapCheckoutFormToCreatePedidoInput,
  guardarUltimoPedidoLocal,
  normalizarCanalMenuee,
} from "../mappers/pedidoMapper";
import { createIdempotencySession } from "../utils/idempotencySession";
import {
  resolverEstadoHorarioPedido,
  horarioValido,
} from "../utils/horariosSucursal";
import { validarCarritoOpciones } from "../mappers/productoMapper";

export function useCartaDigitalLogica(carrito, vaciar, { localId, slug } = {}) {
  const [ubicacionCliente, setUbicacionCliente] = useState(null);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [modoEntrega, setModoEntrega] = useState(null);
  const [costoDelivery, setCostoDelivery] = useState(0);
  const [horariosDisponibles, setHorariosDisponibles] = useState([]);
  const [horarioSeleccionado, setHorarioSeleccionado] = useState("");
  const [pedidosCerrados, setPedidosCerrados] = useState(false);
  const [mensajeCierre, setMensajeCierre] = useState(null);
  const [enviandoPedido, setEnviandoPedido] = useState(false);
  const [inicializandoSucursal, setInicializandoSucursal] = useState(true);
  const [relojMinuto, setRelojMinuto] = useState(() =>
    Math.floor(Date.now() / 60000)
  );
  const idempotencySessionRef = useRef(null);
  const sucursalesRef = useRef([]);
  const sucursalActualRef = useRef(null);
  const carritoRef = useRef(carrito);
  carritoRef.current = carrito;

  if (!idempotencySessionRef.current) {
    idempotencySessionRef.current = createIdempotencySession();
  }

  const aplicarSucursal = useCallback(async (sucursal) => {
    if (!sucursal) {
      sucursalActualRef.current = null;
      setSucursalSeleccionada(null);
      return;
    }

    sucursalActualRef.current = sucursal.id;
    setSucursalSeleccionada(sucursal);
    guardarSucursalIdPersistida(slug, sucursal.id);

    let costoPreview = 0;
    try {
      const config = await obtenerConfiguracionSucursal(sucursal.id);
      if (config?.delivery_activo) {
        const base = Number(config.delivery_base);
        costoPreview = Number.isFinite(base) ? base : 0;
      }
    } catch (error) {
      console.error("Error cargando config_sucursal:", error);
    }

    setCostoDelivery(costoPreview);
  }, [slug]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setRelojMinuto(Math.floor(Date.now() / 60000));
    }, 15000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!sucursalSeleccionada) {
      setHorariosDisponibles([]);
      setHorarioSeleccionado("");
      setPedidosCerrados(false);
      setMensajeCierre(null);
      return;
    }

    const estado = resolverEstadoHorarioPedido(sucursalSeleccionada, {
      modoEntrega,
    });
    setHorariosDisponibles(estado.slots);
    setPedidosCerrados(estado.cerrado);
    setMensajeCierre(estado.mensajeCierre);
    setHorarioSeleccionado((actual) =>
      estado.cerrado || estado.slots.length === 0
        ? ""
        : estado.slots.includes(actual)
          ? actual
          : estado.slots[0]
    );
  }, [sucursalSeleccionada, modoEntrega, relojMinuto]);

  const aplicarSucursalPorDefecto = useCallback(
    async (lista) => {
      const defecto = obtenerSucursalPorDefecto(lista);
      if (defecto) {
        await aplicarSucursal(defecto);
      }
    },
    [aplicarSucursal]
  );

  const cambiarSucursal = useCallback(
    async (sucursal) => {
      if (!sucursal) {
        return false;
      }

      const idActual = sucursalActualRef.current;
      if (
        idActual &&
        String(sucursal.id) !== String(idActual) &&
        carritoRef.current.length > 0
      ) {
        const result = await fireSwal({
          icon: "warning",
          title: "Cambiar sucursal",
          text: "Para evitar inconsistencias se vaciará el carrito actual. ¿Continuar?",
          showCancelButton: true,
          confirmButtonText: "Sí, cambiar",
          cancelButtonText: "Cancelar",
        });
        if (!result.isConfirmed) {
          return false;
        }
        vaciar();
      }

      await aplicarSucursal(sucursal);
      return true;
    },
    [aplicarSucursal, vaciar]
  );

  const usarMiUbicacion = useCallback(
    async ({ silencioso = false } = {}) => {
      const lista = sucursalesRef.current;

      if (!geolocationDisponible()) {
        if (!silencioso) {
          fireSwal(
            "Aviso",
            "Tu navegador no soporta geolocalización. Usamos la sucursal por defecto.",
            "info"
          );
        }
        await aplicarSucursalPorDefecto(lista);
        return false;
      }

      try {
        const ubicacion = await obtenerUbicacionActual();
        setUbicacionCliente(ubicacion);

        const cercana = calcularSucursalMasCercana(ubicacion, lista);

        if (cercana) {
          const aplicada = await cambiarSucursal(cercana);
          if (aplicada && !silencioso) {
            fireSwal({
              icon: "success",
              title: "Sucursal actualizada",
              text: `Seleccionamos ${cercana.nombre} por cercanía.`,
              timer: 2500,
              showConfirmButton: false,
            });
          }
          return aplicada;
        }

        if (!silencioso) {
          fireSwal(
            "Aviso",
            "Ninguna sucursal tiene coordenadas válidas. Usamos la sucursal por defecto.",
            "warning"
          );
        }
        await aplicarSucursalPorDefecto(lista);
        return false;
      } catch (err) {
        if (!silencioso) {
          let mensaje = "No se pudo obtener tu ubicación.";
          if (err?.code === 1 || err?.code === err?.PERMISSION_DENIED) {
            mensaje = "Permiso de ubicación denegado. Puedes elegir sucursal manualmente.";
          } else if (err?.code === 2) {
            mensaje = "Ubicación no disponible. Intenta nuevamente más tarde.";
          } else if (err?.code === 3) {
            mensaje = "Tiempo de espera agotado. Intenta nuevamente.";
          }
          fireSwal("Aviso", mensaje, "info");
        }
        await aplicarSucursalPorDefecto(lista);
        return false;
      }
    },
    [cambiarSucursal, aplicarSucursalPorDefecto]
  );

  const restaurarUbicacionCliente = useCallback(async () => {
    if (!geolocationDisponible()) {
      return false;
    }

    try {
      const ubicacion = await obtenerUbicacionActual();
      setUbicacionCliente(ubicacion);
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelado = false;

    async function inicializarSucursales() {
      if (!localId) {
        sucursalesRef.current = [];
        sucursalActualRef.current = null;
        setSucursales([]);
        setSucursalSeleccionada(null);
        setInicializandoSucursal(false);
        return;
      }

      setInicializandoSucursal(true);

      try {
        const lista = await listarSucursalesDelPortal(localId);
        if (cancelado) return;

        sucursalesRef.current = lista;
        setSucursales(lista);

        if (!lista.length) {
          sucursalActualRef.current = null;
          setSucursalSeleccionada(null);
          return;
        }

        const persistidaId = leerSucursalIdPersistida(slug);
        if (persistidaId) {
          const encontrada = lista.find(
            (s) => String(s.id) === String(persistidaId)
          );
          if (encontrada) {
            await aplicarSucursal(encontrada);
            if (!cancelado) {
              void restaurarUbicacionCliente();
            }
            return;
          }
        }

        if (lista.length === 1) {
          await aplicarSucursal(lista[0]);
          if (!cancelado) {
            void restaurarUbicacionCliente();
          }
          return;
        }

        await usarMiUbicacion({ silencioso: true });
      } catch (error) {
        console.error("Error inicializando sucursales:", error);
        if (!cancelado) {
          await aplicarSucursalPorDefecto(sucursalesRef.current);
        }
      } finally {
        if (!cancelado) {
          setInicializandoSucursal(false);
        }
      }
    }

    inicializarSucursales();

    return () => {
      cancelado = true;
    };
  }, [
    localId,
    slug,
    aplicarSucursal,
    aplicarSucursalPorDefecto,
    usarMiUbicacion,
    restaurarUbicacionCliente,
  ]);

  async function seleccionarSucursalManual(sucursalId) {
    const encontrada = sucursales.find((s) => String(s.id) === String(sucursalId));
    if (!encontrada) return false;
    return cambiarSucursal(encontrada);
  }

  async function finalizarPedido(datosCheckout = null) {
    if (!sucursalSeleccionada) {
      fireSwal("Error", "No hay sucursal seleccionada", "error");
      return false;
    }

    if (!modoEntrega) {
      fireSwal("Error", "Selecciona retiro o delivery", "error");
      return false;
    }

    if (carrito.length === 0) {
      fireSwal("Error", "El carrito está vacío", "error");
      return false;
    }

    const errorOpciones = validarCarritoOpciones(carrito);
    if (errorOpciones) {
      fireSwal("Error", errorOpciones, "error");
      return false;
    }

    const nombre =
      datosCheckout?.cliente?.nombre ?? datosCheckout?.nombre ?? "";
    const telefono =
      datosCheckout?.cliente?.telefono ?? datosCheckout?.telefono ?? "";

    if (!String(nombre).trim() || !String(telefono).trim()) {
      fireSwal("Error", "Faltan datos del cliente", "error");
      return false;
    }

    if (pedidosCerrados) {
      fireSwal(
        "Cerrado",
        mensajeCierre || "Estamos cerrados. Podrás pedir el próximo día de atención.",
        "info"
      );
      return false;
    }

    if (!horarioValido(horarioSeleccionado, horariosDisponibles)) {
      fireSwal("Error", "Selecciona un horario valido", "error");
      return false;
    }

    try {
      normalizarCanalMenuee(datosCheckout?.canal || modoEntrega);
    } catch {
      fireSwal("Error", "Canal de entrega no válido", "error");
      return false;
    }

    if (modoEntrega === "domicilio") {
      const direccionTexto =
        datosCheckout?.direccion?.texto ??
        datosCheckout?.direccion ??
        "";
      if (!String(direccionTexto).trim()) {
        fireSwal("Error", "La dirección de entrega es obligatoria", "error");
        return false;
      }
    }

    const session = idempotencySessionRef.current;

    if (session.isInFlight()) {
      fireSwal("Aviso", "Tu pedido ya se está enviando…", "info");
      return false;
    }

    const idempotencyKey = session.acquire();
    setEnviandoPedido(true);

    try {
      const input = mapCheckoutFormToCreatePedidoInput({
        datosCheckout,
        carrito,
        sucursalId: sucursalSeleccionada.id,
        modoEntrega,
        horarioSeleccionado,
      });

      const pedido = await createPedido({
        ...input,
        idempotencyKey,
      });

      guardarUltimoPedidoLocal(pedido, carrito);
      vaciar();
      session.release(true);

      return pedido;
    } catch (err) {
      session.release(false);
      console.error("Error enviando pedido E6:", err);

      const mensaje =
        err instanceof RpcClientError
          ? err.message
          : err?.message || "Hubo un error al enviar el pedido. Intenta nuevamente.";

      fireSwal("Error", mensaje, "error");
      return false;
    } finally {
      setEnviandoPedido(false);
    }
  }

  return {
    ubicacionCliente,
    sucursales,
    sucursalSeleccionada,
    seleccionarSucursalManual,
    modoEntrega,
    setModoEntrega,
    costoDelivery,
    horariosDisponibles,
    horarioSeleccionado,
    setHorarioSeleccionado,
    pedidosCerrados,
    mensajeCierre,
    finalizarPedido,
    enviandoPedido,
    inicializandoSucursal,
  };
}
